import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { trackEvent } from '../lib/clarity';
import { Eye, EyeOff, LogIn, School, User, Lock, AlertCircle, UserPlus, KeyRound, Hash, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

const SUPER_ADMIN_EMAILS = ['anubhavklugun@gmail.com', 'bhardwajvaibhav82@gmail.com'];

const ROLES = [
  { id: 'admin', label: 'Admin', description: 'School administration' },
  { id: 'teacher', label: 'Teacher', description: 'Teaching & classes' },
  { id: 'student', label: 'Student', description: 'Learning access' },
  { id: 'parent', label: 'Parent', description: 'Child monitoring' },
  { id: 'finance_admin', label: 'Finance Admin', description: 'Financial management' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginIndividual, loginWithGoogleSession } = useApp();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Default to 'school' tab; switch to 'individual' when the entry URL is
  // /login?tab=user (e.g. when the landing page sends teachers here).
  const initialTab = (() => {
    try {
      const t = new URLSearchParams(location.search).get('tab');
      return t === 'user' || t === 'individual' ? 'individual' : 'school';
    } catch { return 'school'; }
  })();
  const [identityType, setIdentityType] = useState(initialTab); // 'school' | 'individual'

  const [formData, setFormData] = useState({
    identityCode: '', // holds School Code OR User Code
    role: '',
    email: '',
    password: '',
  });

  // Track explicit OAuth-processing state so we render a "Signing you in…"
  // overlay instead of leaving the form looking idle while the callback runs.
  const [oauthBusy, setOauthBusy] = useState(false);

  // Google OAuth landing — the synchronous handler at the top of App.js
  // has already persisted token + user to localStorage and stashed a
  // `sip_oauth_dest` in sessionStorage (or `sip_oauth_error` on failure).
  // We just need to (1) flip AppContext's isAuthenticated and (2) navigate
  // to the right dashboard. Both cases — the success-redirect and the
  // explicit `?error=` cancel-banner — are handled here.
  useEffect(() => {
    const url = new URL(window.location.href);

    // ── Error path ──
    // sessionStorage-stashed error from the App.js handler (e.g. base64
    // parse failure), OR the query-string `?error=` returned by Google or
    // by our /api/auth/google/callback wrapper for access_denied etc.
    const errStash = sessionStorage.getItem('sip_oauth_error');
    if (errStash) sessionStorage.removeItem('sip_oauth_error');
    const errCode = url.searchParams.get('error') || errStash;
    if (errCode) {
      const errMsg = url.searchParams.get('msg') || '';
      let friendly;
      if (errCode === 'access_denied') {
        friendly = 'Google sign-in was cancelled. Click "Continue with Google" again whenever you\'re ready.';
      } else if (errCode === 'SCHOOL_USER_GOOGLE') {
        friendly = errMsg || 'This email is registered to a school. Please sign in with your School Code.';
      } else if (errCode === 'FREE_TIER_DEVICE_REUSE') {
        friendly = 'A free account already exists on this device. Sign in with that account or upgrade.';
      } else if (errCode === 'FREE_TIER_IP_LIMIT') {
        friendly = errMsg || 'Too many free accounts from this network. Please contact support.';
      } else if (errCode === 'missing_code') {
        friendly = 'Google sign-in did not complete. Please try again.';
      } else {
        friendly = errMsg || 'Google sign-in failed. Please try again.';
      }
      setError(friendly);
      // Clean the URL so a refresh doesn't replay the error banner.
      url.searchParams.delete('error');
      url.searchParams.delete('msg');
      window.history.replaceState({}, '', url.pathname + (url.search || ''));
      return;
    }

    // ── Success path ──
    const dest = sessionStorage.getItem('sip_oauth_dest');
    if (!dest) return;
    sessionStorage.removeItem('sip_oauth_dest');
    setOauthBusy(true);
    // Hydrate AppContext state from the JWT + user that App.js already
    // persisted, then navigate. `loginWithGoogleSession` is idempotent
    // — it reads from localStorage if the values are already there.
    try {
      const tokenLs = localStorage.getItem('sip_token');
      const userLs = localStorage.getItem('sip_user');
      if (tokenLs && userLs) {
        const userObj = JSON.parse(userLs);
        loginWithGoogleSession(tokenLs, userObj).finally(() => {
          navigate(dest, { replace: true });
          setOauthBusy(false);
        });
      } else {
        setOauthBusy(false);
        navigate(dest, { replace: true });
      }
    } catch (err) {
      setError(err?.message || 'Google sign-in failed. Please try again.');
      setOauthBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSuperAdminEmail = SUPER_ADMIN_EMAILS.includes(formData.email.toLowerCase());
  const availableRoles = isSuperAdminEmail
    ? [{ id: 'super_admin', label: 'Super Admin', description: 'System administration' }, ...ROLES]
    : ROLES;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { identityCode, role, email, password } = formData;
      if (!email || !password) throw new Error('Email and password are required');

      if (identityType === 'individual') {
        if (!identityCode) throw new Error('User Code is required (e.g. USR123456)');
        const data = await loginIndividual(email, identityCode.toUpperCase(), password);
        trackEvent('login_success', { method: 'individual' });
        navigate(data.user?.schoolId ? '/dashboard' : '/teacher-assignments', { replace: true });
        return;
      }

      // School mode
      const code = role === 'super_admin' ? 'SYSTEM' : identityCode;
      if (role !== 'super_admin' && !identityCode) throw new Error('School code is required');
      if (!role) throw new Error('Please select a role');
      await login(code, role, email, password);
      trackEvent('login_success', { method: 'school', role });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Incorrect credentials');
    } finally {
      setIsLoading(false);
    }
  };

  // Custom Google OAuth — server-side Authorization Code flow. We send
  // the user's browser to our own /api/auth/google/start endpoint, which
  // signs a state token (carrying the deviceId), then 302-redirects to
  // Google's consent screen. Google then redirects back to
  // /api/auth/google/callback, which finishes the exchange and lands us
  // back at /login with the JWT in the URL fragment.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    setError('');
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) {
      setError('Backend URL is not configured.');
      return;
    }
    const deviceId = api._getOrCreateDeviceId();
    const qs = new URLSearchParams({ device_id: deviceId, return_to: '/login' });
    window.location.href = `${backend}/api/auth/google/start?${qs.toString()}`;
  };

  // Resend User Code to the email on record. Anti-enumeration: backend
  // always returns success regardless of whether the email exists, so we
  // surface a single neutral message either way. Rate-limit (429) is
  // surfaced verbatim so the teacher knows they're capped.
  const [resendBusy, setResendBusy] = useState(false);
  const handleResendUserCode = async () => {
    const email = (formData.email || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter your email above first, then click "Forgot User Code?".');
      return;
    }
    setResendBusy(true);
    setError('');
    try {
      await api.resendUserCode(email);
      toast.success(`If ${email} is registered, your User Code has been emailed to you.`);
    } catch (err) {
      if (err?.status === 429 || /Too many requests/i.test(err?.message || '')) {
        setError(err.message || 'Too many requests. Try again in 24 hours.');
      } else {
        setError(err.message || 'Could not send User Code. Please try again.');
      }
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* OAuth-return overlay — full-screen while the Google
          callback finishes exchanging the auth code for our app
          token. Prevents the user from seeing a half-loaded form
          and stops them clicking Google again mid-exchange. */}
      {oauthBusy && (
        <div
          data-testid="oauth-busy-overlay"
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm"
        >
          <div className="w-12 h-12 border-4 border-[#00b0f0] border-t-transparent rounded-full animate-spin mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Signing you in…</h2>
          <p className="text-sm text-muted-foreground mt-1">Verifying your Google account, this takes a few seconds.</p>
        </div>
      )}
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1f3354] flex-col items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <img
            src="/brand/logo.png"
            alt="ActanonEdu"
            className="w-36 h-36 mx-auto mb-8 rounded-2xl shadow-xl ring-1 ring-white/10"
          />
          <h1 className="text-4xl font-heading font-bold text-white mb-4">
            Student Intelligence Platform
          </h1>
          <p className="text-white/70 text-lg mb-2">
            Designed by{' '}
            <span className="text-[#00b0f0] font-semibold">Actanon Technologies</span>{' '}
          </p>
          <p className="text-white/50 text-sm max-w-md mx-auto mt-6">
            Sign in with your School Code if you're part of an institution, or with your User Code
            if you're an independent teacher.
          </p>
        </motion.div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-slate-50 dark:bg-slate-950">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Back to homepage */}
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4 group"
            data-testid="back-to-home"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to homepage
          </button>

          <div className="lg:hidden text-center mb-8">
            <h1 className="text-2xl font-heading font-bold text-brand-deep-blue">
              Student Intelligence Platform
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Sign in to continue</p>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-soft-lg p-8 border border-slate-200 dark:border-slate-800">
            <div className="text-center mb-6">
              <img
                src="/brand/logo.png"
                alt="ActanonEdu"
                className="w-14 h-14 mx-auto mb-3 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
              />
              <h2 className="text-2xl font-heading font-bold text-foreground">Welcome</h2>
              <p className="text-muted-foreground text-sm mt-1">Sign in to your account</p>
            </div>

            {/* Identity type toggle */}
            <div
              className="grid grid-cols-2 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 mb-5"
              data-testid="identity-toggle"
            >
              <button
                type="button"
                onClick={() => { setIdentityType('school'); setError(''); }}
                className={`py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  identityType === 'school'
                    ? 'bg-white dark:bg-slate-900 shadow-sm text-brand-deep-blue'
                    : 'text-muted-foreground'
                }`}
                data-testid="identity-school"
              >
                School Code
              </button>
              <button
                type="button"
                onClick={() => { setIdentityType('individual'); setError(''); }}
                className={`py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  identityType === 'individual'
                    ? 'bg-white dark:bg-slate-900 shadow-sm text-brand-deep-blue'
                    : 'text-muted-foreground'
                }`}
                data-testid="identity-individual"
              >
                User Code
              </button>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription data-testid="login-error">{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="pl-10"
                    data-testid="login-email"
                    required
                  />
                </div>
              </div>

              {/* School Code / User Code */}
              {formData.role !== 'super_admin' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="identityCode">
                      {identityType === 'individual' ? 'User Code' : 'School Code'}
                    </Label>
                    {identityType === 'individual' && (
                      <button
                        type="button"
                        onClick={handleResendUserCode}
                        disabled={resendBusy}
                        className="text-[11px] text-brand-deep-blue hover:underline font-medium disabled:opacity-50 disabled:no-underline"
                        data-testid="resend-user-code-link"
                      >
                        {resendBusy ? 'Sending…' : 'Forgot User Code?'}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    {identityType === 'individual' ? (
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    ) : (
                      <School className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    )}
                    <Input
                      id="identityCode"
                      type="text"
                      placeholder={
                        identityType === 'individual' ? 'USR123456' : 'Enter school code (e.g., DEMO01)'
                      }
                      value={formData.identityCode}
                      onChange={(e) => handleChange('identityCode', e.target.value.toUpperCase())}
                      className="pl-10 uppercase"
                      data-testid="login-identity-code"
                    />
                  </div>
                </div>
              )}

              {/* Role (school only) */}
              {identityType === 'school' && (
                <div className="space-y-2">
                  <Label htmlFor="role">Select Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => handleChange('role', value)}
                  >
                    <SelectTrigger data-testid="login-role">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{role.label}</span>
                            <span className="text-xs text-muted-foreground">{role.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {identityType === 'individual' && (
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-[11px] text-brand-deep-blue hover:underline font-medium"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="pl-10 pr-10"
                    data-testid="login-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full bg-brand-deep-blue hover:bg-brand-deep-blue/90 h-11"
                disabled={isLoading}
                data-testid="login-submit"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Sign In
                  </div>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">or</span>
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
            </div>

            {/* Google OAuth — server-side Authorization Code flow via our own GOOGLE_CLIENT_ID. */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 gap-2"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              data-testid="google-signin-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </Button>

            {/* Sign up link */}
            <div className="text-center mt-5 text-sm text-muted-foreground">
              New teacher?{' '}
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="text-brand-deep-blue font-semibold hover:underline inline-flex items-center gap-1"
                data-testid="signup-link"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Create an account
              </button>
            </div>

            {/* Legal footer — DPDP / Google OAuth verification requires
                a clear consent line linking Terms + Privacy at signup. */}
            <p className="text-center mt-5 text-[11px] leading-relaxed text-muted-foreground">
              By continuing, you agree to our{' '}
              <button
                type="button"
                onClick={() => navigate('/terms')}
                className="text-brand-deep-blue font-medium hover:underline"
                data-testid="login-terms-link"
              >
                Terms of Service
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() => navigate('/privacy')}
                className="text-brand-deep-blue font-medium hover:underline"
                data-testid="login-privacy-link"
              >
                Privacy Policy
              </button>
              .
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
